import { guidelineData } from '../data/guidelines';

/**
 * Which guideline owns a given algorithm page id (e.g. "BCEL-3" -> "dlbcl").
 * Lets a reference node point at a page in another guideline instead of
 * silently landing the user on a blank canvas.
 */
export function findGuidelineForPage(pageId) {
  if (!pageId) return null;
  for (const [id, data] of Object.entries(guidelineData)) {
    const pages = data.meta?.pages || [];
    if (pages.some((p) => (typeof p === 'string' ? p : p.id) === pageId)) return id;
  }
  return null;
}

export async function loadGuideline(guidelineId) {
  const data = guidelineData[guidelineId];
  if (!data) {
    throw new Error(`Guideline "${guidelineId}" not found`);
  }

  const { meta, nodes, edges } = data;

  // Basic validation
  const nodeIds = new Set(nodes.map((n) => n.id));

  const errors = [];
  for (const edge of edges) {
    if (!nodeIds.has(edge.source)) {
      errors.push(`Edge "${edge.id}" references unknown source "${edge.source}"`);
    }
    if (!nodeIds.has(edge.target)) {
      errors.push(`Edge "${edge.id}" references unknown target "${edge.target}"`);
    }
  }

  for (const node of nodes) {
    if (
      !node.position ||
      typeof node.position.x !== 'number' ||
      typeof node.position.y !== 'number'
    ) {
      errors.push(`Node "${node.id}" missing valid position { x, y }`);
    }
    if (!node.page) {
      errors.push(`Node "${node.id}" missing page assignment`);
    }
  }

  // Decision options must each resolve to an edge, or the walker will show a
  // different set of choices than the flowchart does.
  for (const node of nodes) {
    if (node.type !== 'decision' || !node.options) continue;
    const outgoing = edges.filter((e) => e.source === node.id);
    const hasConvergentDefault =
      outgoing.filter((e) => typeof e.conditionIndex !== 'number').length === 1;
    node.options.forEach((opt, idx) => {
      const mapped = outgoing.some((e) => e.conditionIndex === idx);
      if (!mapped && !hasConvergentDefault) {
        errors.push(
          `Decision "${node.id}" option ${idx} ("${opt.label}") has no outgoing edge`
        );
      }
    });
  }

  if (errors.length > 0) {
    console.warn('Guideline validation warnings:', errors);
  }

  return {
    ...meta,
    nodes,
    edges,
    validationErrors: errors,
  };
}
